
import Head from '../components/Head'
import Footer from '../components/Footer'
import {
    Card, Text, Center, createStyles, rem,
    Space, List, NumberInput, Slider, Switch,
    Button, Group, Select, Anchor, Stack, Divider, Badge, Box, Table, SimpleGrid
} from '@mantine/core'
import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { IconTag, IconCalculator, IconArrowsLeftRight, IconTicket, IconReceipt } from '@tabler/icons-react'

// ── Animated number hook ────────────────────────────────────────────────────
function useCountUp(target: number, duration = 400): number {
    const [display, setDisplay] = useState(target)
    const rafRef = useRef<number | null>(null)
    const startRef = useRef<number | null>(null)
    const fromRef = useRef(target)

    useEffect(() => {
        const from = fromRef.current
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        startRef.current = null

        const step = (ts: number) => {
            if (startRef.current === null) startRef.current = ts
            const progress = Math.min((ts - startRef.current) / duration, 1)
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3)
            setDisplay(from + (target - from) * eased)
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(step)
            } else {
                fromRef.current = target
            }
        }
        rafRef.current = requestAnimationFrame(step)
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target])

    return display
}

// ── Styles ──────────────────────────────────────────────────────────────────
const useStyles = createStyles((theme) => ({

    heroWrapper: {
        background: 'radial-gradient(ellipse at 50% 60%, #d6f5d6 0%, #e8f6fb 50%, #f4fbff 100%)',
        paddingTop: rem(32),
        paddingBottom: rem(40),
        textAlign: 'center',
    },

    pageTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontSize: rem(72),
        color: '#155724',
        lineHeight: 1.1,

        [theme.fn.smallerThan('md')]: { fontSize: rem(52) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(36) },
    },

    pageSubtitle: {
        fontSize: rem(18),
        color: theme.colors.gray[6],
        marginTop: rem(8),
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
    },

    // ── Section divider label ─────────────────────────────────────────────
    dividerLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(12),
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.colors.gray[5],
    },

    sectionDivider: {
        margin: `${rem(36)} 0 ${rem(28)}`,
    },

    // ── Cards ─────────────────────────────────────────────────────────────
    card: {
        width: '100%',
        maxWidth: rem(820),
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
            boxShadow: theme.shadows.md,
            transform: 'translateY(-2px)',
        },
    },

    cardTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(28),
        textAlign: 'center',

        [theme.fn.smallerThan('sm')]: { fontSize: rem(20) },
    },

    // ── Calculator sections & Result rows ─────────────────────────────────
    calcSection: {
        background: theme.colors.gray[0],
        borderRadius: rem(14),
        padding: rem(16),
        border: `1px solid ${theme.colors.gray[2]}`,
        display: 'flex',
        flexDirection: 'column',
    },

    calcSectionHeader: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(13),
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        gap: rem(8),
        paddingBottom: rem(10),
        marginBottom: rem(4),
        borderBottom: `1px solid ${theme.colors.gray[2]}`,
    },

    resultRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: rem(8),
        padding: `${rem(7)} 0`,
        borderBottom: `1px solid ${theme.colors.gray[2]}`,

        [theme.fn.smallerThan('sm')]: {
            gap: rem(6),
            padding: `${rem(6)} 0`,
        },
    },

    resultLabel: {
        color: theme.colors.gray[7],
        fontSize: rem(13.5),
    },

    resultValue: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(18),
        color: theme.colors.dark[7],
        transition: 'color 0.3s ease',
        textAlign: 'right',
        marginLeft: 'auto',

        [theme.fn.smallerThan('sm')]: {
            fontSize: rem(17),
        },
    },

    // ── Savings callout ───────────────────────────────────────────────────
    savingsCallout: {
        background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
        border: `2px solid #28a745`,
        borderRadius: rem(16),
        padding: `${rem(20)} ${rem(28)}`,
        textAlign: 'center',
        marginTop: rem(20),
    },

    savingsLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 600,
        fontSize: rem(14),
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#155724',
    },

    savingsAmount: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        fontSize: rem(52),
        color: '#155724',
        lineHeight: 1.1,

        [theme.fn.smallerThan('sm')]: { fontSize: rem(36) },
    },

    savingsSubtext: {
        fontSize: rem(13),
        color: '#1e7e34',
        marginTop: rem(4),
    },

    // ── Comparison table ──────────────────────────────────────────────────
    compTable: {
        '& thead th': {
            fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
            fontWeight: 700,
            fontSize: rem(15),
            textAlign: 'center',
            background: 'transparent',
        },
        '& tbody td': {
            textAlign: 'center',
            fontSize: rem(14),
            verticalAlign: 'middle',
        },
        '& tbody tr:hover': {
            background: theme.colors.gray[0],
        },
    },

    csCell: {
        color: '#155724',
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
    },

    ebCell: {
        color: theme.colors.gray[7],
    },

    // ── CTA button ────────────────────────────────────────────────────────
    ctaButton: {
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 8px 24px rgba(0,140,0,0.3)',
        },
    },
}))

// ── Divider helper ───────────────────────────────────────────────────────────
function SectionDivider({ label }: { label: string }) {
    const { classes } = useStyles()
    return (
        <Divider
            className={classes.sectionDivider}
            label={<span className={classes.dividerLabel}>{label}</span>}
            labelPosition="center"
            size="xs"
        />
    )
}

// ── Static data ──────────────────────────────────────────────────────────────
const links = [
    { 'link': '/About', 'label': 'About' },
    { 'link': '/Support', 'label': 'Support' },
    { 'link': '/Pp', 'label': 'Privacy' },
    { 'link': '/Tos', 'label': 'Terms of Use' },
]

// ── Main component ───────────────────────────────────────────────────────────
function Pricing() {
    const { classes } = useStyles()

    const [inputPrice, setInputPrice] = useState<number | "">(10)
    const [inputSold, setInputSold] = useState<number>(100)
    const [costTotal, setCostTotal] = useState<number>(0)
    const [costPerTicket, setCostPerTicket] = useState<number>(0)
    const [stripe, setStripe] = useState<boolean>(false)
    const [eventbriteCost, setEventbriteCost] = useState<number>(216)
    const [eventbriteCostPerTicket, setEventbriteCostPerTicket] = useState<number>(0)
    const [hybridCost, setHybridCost] = useState<number>(0)
    const [hybridCostPerTicket, setHybridCostPerTicket] = useState<number>(0)

    const [costPerTicketPercent, setCostPerTicketPercent] = useState<number>(0)
    const [eventbriteCostPerTicketPercent, setEventbriteCostPerTicketPercent] = useState<number>(0)
    const [hybridCostPerTicketPercent, setHybridCostPerTicketPercent] = useState<number>(0)
    const [totalRevenue, setTotalRevenue] = useState<number>(0)

    const [csFeeTotal, setCsFeeTotal] = useState<number>(0)
    const [stripeFeeTotal, setStripeFeeTotal] = useState<number>(0)
    const [stripeFeePerTicket, setStripeFeePerTicket] = useState<number>(0)
    const [endPricePerTicket, setEndPricePerTicket] = useState<number>(0)

    const fee = .12

    const [avgTickets, setAvgTickets] = useState<string | null | undefined>('2')
    const [evPercent, setEvPercent] = useState<number | "">(30)

    const averageTicketOptions = [
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
    ]

    const getCost = () => {
        if (typeof (inputPrice) === 'number' && typeof (evPercent) === 'number') {
            if (stripe === false) {
                const cscost = inputSold * fee
                const ebcost = (inputPrice * inputSold * .037) + (1.79 * inputSold)
                const hycost = (inputSold * evPercent * .01) * inputPrice * .037 + (1.79 * inputSold * evPercent * .01) + (inputSold * (1 - evPercent * .01)) * fee
                const totrevenue = inputPrice * inputSold

                setTotalRevenue(totrevenue)
                setCostTotal(cscost)
                setCsFeeTotal(cscost)
                setStripeFeeTotal(0)
                setStripeFeePerTicket(0)
                setEventbriteCost(ebcost)
                setHybridCost(hycost)
                setCostPerTicket(fee)
                setEventbriteCostPerTicket(ebcost / inputSold)
                setHybridCostPerTicket(hycost / inputSold)
                setCostPerTicketPercent((cscost) / totrevenue)
                setEventbriteCostPerTicketPercent(ebcost / totrevenue)
                setHybridCostPerTicketPercent(hycost / totrevenue)
                setEndPricePerTicket(inputPrice + fee)
            } else {
                const effectiveAvgTickets = Math.max(1, Math.min(Number(avgTickets) || 1, inputSold))
                const csFee = inputSold * fee
                const subtotalPerTicket = inputPrice + fee
                const grossPerTicket = (subtotalPerTicket + (.3 / effectiveAvgTickets)) / 0.971
                const stripePerTicket = (grossPerTicket * .029) + (.3 / effectiveAvgTickets)
                const stripeTotal = stripePerTicket * inputSold

                const cscost = csFee + stripeTotal
                const ebcost = (inputPrice * inputSold * .037) + (1.79 * inputSold) + ((inputPrice * inputSold)) * .029
                const hycost = (inputSold * evPercent * .01) * inputPrice * .037 + (1.79 * inputSold * evPercent * .01) +
                    ((inputPrice * inputSold * evPercent * .01)) * .029 +
                    (inputSold * (1 - evPercent * .01) * fee) +
                    (inputSold * (1 - evPercent * .01) * stripePerTicket)
                const totrevenue = inputPrice * inputSold

                setTotalRevenue(totrevenue)
                setCostTotal(cscost)
                setCsFeeTotal(csFee)
                setStripeFeeTotal(stripeTotal)
                setStripeFeePerTicket(stripePerTicket)
                setEventbriteCost(ebcost)
                setHybridCost(hycost)
                setCostPerTicket(fee)
                setEventbriteCostPerTicket(ebcost / inputSold)
                setHybridCostPerTicket(hycost / inputSold)
                setCostPerTicketPercent((cscost) / totrevenue)
                setEventbriteCostPerTicketPercent(ebcost / totrevenue)
                setHybridCostPerTicketPercent(hycost / totrevenue)
                setEndPricePerTicket(grossPerTicket)
            }
        }
    }

    useEffect(() => {
        getCost()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputPrice, inputSold, stripe, evPercent, avgTickets])

    // Animated display values
    const animTotalRevenue = useCountUp(totalRevenue)
    const animCostTotal = useCountUp(costTotal)
    const animCsFeeTotal = useCountUp(csFeeTotal)
    const animStripeFeeTotal = useCountUp(stripeFeeTotal)
    const animStripeFeePerTicket = useCountUp(stripeFeePerTicket)
    const animEventbriteCost = useCountUp(eventbriteCost)
    const animHybridCost = useCountUp(hybridCost)
    const animCostPerTicket = useCountUp(costPerTicket)
    const animEventbriteCostPerTicket = useCountUp(eventbriteCostPerTicket)
    const animHybridCostPerTicket = useCountUp(hybridCostPerTicket)
    const animCostPct = useCountUp(costPerTicketPercent * 100)
    const animEbPct = useCountUp(eventbriteCostPerTicketPercent * 100)
    const animHyPct = useCountUp(hybridCostPerTicketPercent * 100)
    const savings = useCountUp(eventbriteCost - costTotal)
    const hybridSavings = useCountUp(eventbriteCost - hybridCost)
    const animEndPricePerTicket = useCountUp(endPricePerTicket)
    const animInputPrice = useCountUp(typeof inputPrice === 'number' ? inputPrice : 0)

    return (
        <body>
            <Head />

            {/* ── Hero ── */}
            <div className={classes.heroWrapper}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: rem(10), marginBottom: rem(12) }}>
                    <IconTag size={36} color="#155724" />
                </div>
                <Text className={classes.pageTitle}>Low Cost.&nbsp; No Joke.</Text>
                <Text className={classes.pageSubtitle}>
                    Transparent, flat-rate ticketing — no hidden fees, no surprises.
                </Text>
            </div>

            <SectionDivider label="Our pricing" />

            {/* ── 12 cents flat-rate card ── */}
            <Center>
                <Card
                    padding='xl'
                    radius='xl'
                    withBorder
                    shadow='sm'
                    className={classes.card}
                    sx={{ borderLeft: '4px solid #28a745' }}
                >
                    <Group spacing="xs" position="center" mb="xs">
                        <IconTicket size={22} color="#28a745" />
                        <Text className={classes.cardTitle}>Lock in 12¢ per ticket!</Text>
                    </Group>

                    <Center mb="xs">
                        <Badge color="green" variant="filled" size="lg" radius="sm">
                            Flat rate · No percentage fees
                        </Badge>
                    </Center>

                    <List spacing='xs' px='lg' mt="md">
                        <List.Item>One simple, transparent and unbeatable price per ticket.</List.Item>
                        <List.Item>
                            <Anchor href="https://en.wikipedia.org/wiki/Enshittification" target="_blank">Enshittification</Anchor>
                            {' '}proof pricing — even when prices change, you lock in the rate from your first sale.
                        </List.Item>
                        <List.Item>We will always be positioned to offer the best value ticketing for comedians.</List.Item>
                        <List.Item>Fans can discover your event right on our platform.</List.Item>
                        <List.Item>Cost can be passed to fans or absorbed by the organizer.</List.Item>
                        <List.Item>Reinvest the savings into better shows and stronger promotion.</List.Item>
                    </List>
                </Card>
            </Center>

            <SectionDivider label="Fee calculator" />

            {/* ── Fee Calculator card ── */}
            <Center>
                <Card
                    padding='xl'
                    radius='xl'
                    withBorder
                    shadow='sm'
                    className={classes.card}
                    sx={{ borderLeft: '4px solid #2b9eb3' }}
                >
                    <Group spacing="xs" position="center" mb="xs">
                        <IconCalculator size={22} color="#2b9eb3" />
                        <Text className={classes.cardTitle}>Fee Calculator</Text>
                    </Group>

                    <Stack spacing="md" mt="md">
                        <Center>
                            <Stack spacing={4} align="center">
                                <Text size="sm" color="dimmed">Include Stripe processing fees?</Text>
                                <Switch
                                    checked={stripe}
                                    onChange={(event) => setStripe(event.currentTarget.checked)}
                                    color="teal"
                                />
                                <Text sx={{ fontSize: '.75rem' }} color="dimmed">(we are partnered with Stripe)</Text>
                            </Stack>
                        </Center>

                        <Group position="center" align="center">
                            <Text size="sm">Ticket price in $</Text>
                            <NumberInput
                                value={inputPrice}
                                onChange={setInputPrice}
                                sx={{ width: rem(80) }}
                                min={1}
                            />
                        </Group>

                        {stripe && inputSold > 1 && (
                            <Group position="center" align="center">
                                <Text size="sm">Avg. tickets per transaction</Text>
                                <Select
                                    value={avgTickets}
                                    onChange={setAvgTickets}
                                    placeholder="Select"
                                    data={averageTicketOptions}
                                    searchable={false}
                                    clearable={false}
                                    sx={{ width: rem(80) }}
                                />
                            </Group>
                        )}

                        <div>
                            <Text size="sm" mb={6}>Tickets sold: <strong>{inputSold}</strong></Text>
                            <Slider
                                min={1}
                                max={100}
                                step={1}
                                value={inputSold}
                                onChange={(val) => setInputSold(Math.min(100, Math.max(1, val)))}
                                color="teal"
                                marks={[
                                    { value: 10, label: '10' },
                                    { value: 20, label: '20' },
                                    { value: 30, label: '30' },
                                    { value: 40, label: '40' },
                                    { value: 50, label: '50' },
                                    { value: 60, label: '60' },
                                    { value: 70, label: '70' },
                                    { value: 80, label: '80' },
                                    { value: 90, label: '90' },
                                    { value: 100, label: '100' },
                                ]}
                            />
                        </div>

                        <Space h="sm" />

                        {/* Calculations separated into Per Ticket and Event Totals */}
                        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                            {/* ── Per-Ticket Column ── */}
                            <Box className={classes.calcSection} sx={{ borderTop: '3px solid #2b9eb3' }}>
                                <div className={classes.calcSectionHeader}>
                                    <IconTicket size={18} color="#2b9eb3" />
                                    <Text weight={700} size="sm" sx={{ color: '#2b9eb3' }}>Per Ticket</Text>
                                </div>
                                <Stack spacing={0}>
                                    <div className={classes.resultRow}>
                                        <Text className={classes.resultLabel}>Base ticket price</Text>
                                        <Text className={classes.resultValue}>${animInputPrice.toFixed(2)}</Text>
                                    </div>

                                    {stripe ? (
                                        <>
                                            <div className={classes.resultRow}>
                                                <Text className={classes.resultLabel}>Comedy Seen fee</Text>
                                                <Text className={classes.resultValue}>${animCostPerTicket.toFixed(2)}</Text>
                                            </div>
                                            <div className={classes.resultRow}>
                                                <Text className={classes.resultLabel}>Stripe processing fee</Text>
                                                <Text className={classes.resultValue}>${animStripeFeePerTicket.toFixed(2)}</Text>
                                            </div>
                                            <div className={classes.resultRow}>
                                                <Text className={classes.resultLabel}>Combined fees</Text>
                                                <Text className={classes.resultValue}>${(animCostPerTicket + animStripeFeePerTicket).toFixed(2)}</Text>
                                            </div>
                                        </>
                                    ) : (
                                        <div className={classes.resultRow}>
                                            <Text className={classes.resultLabel}>Comedy Seen fee</Text>
                                            <Text className={classes.resultValue}>${animCostPerTicket.toFixed(2)}</Text>
                                        </div>
                                    )}

                                    <div className={classes.resultRow} style={{ borderBottom: 'none' }}>
                                        <Text className={classes.resultLabel} weight={600} color="dark">End price including fees</Text>
                                        <Text className={classes.resultValue} sx={{ color: '#2b9eb3', fontWeight: 800 }}>${animEndPricePerTicket.toFixed(2)}</Text>
                                    </div>
                                </Stack>
                            </Box>

                            {/* ── Event Totals Column ── */}
                            <Box className={classes.calcSection} sx={{ borderTop: '3px solid #28a745' }}>
                                <div className={classes.calcSectionHeader}>
                                    <IconReceipt size={18} color="#28a745" />
                                    <Text weight={700} size="sm" sx={{ color: '#1e7e34' }}>Event Totals ({inputSold} {inputSold === 1 ? 'ticket' : 'tickets'})</Text>
                                </div>
                                <Stack spacing={0}>
                                    <div className={classes.resultRow}>
                                        <Text className={classes.resultLabel}>Total revenue generated</Text>
                                        <Text className={classes.resultValue} sx={{ color: '#28a745' }}>${animTotalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                    </div>

                                    {stripe ? (
                                        <>
                                            <div className={classes.resultRow}>
                                                <Text className={classes.resultLabel}>Comedy Seen total fees</Text>
                                                <Text className={classes.resultValue}>${animCsFeeTotal.toFixed(2)}</Text>
                                            </div>
                                            <div className={classes.resultRow}>
                                                <Text className={classes.resultLabel}>Stripe processing total fees</Text>
                                                <Text className={classes.resultValue}>${animStripeFeeTotal.toFixed(2)}</Text>
                                            </div>
                                            <div className={classes.resultRow}>
                                                <Text className={classes.resultLabel}>Combined total fees</Text>
                                                <Text className={classes.resultValue}>${animCostTotal.toFixed(2)}</Text>
                                            </div>
                                        </>
                                    ) : (
                                        <div className={classes.resultRow}>
                                            <Text className={classes.resultLabel}>Total fees (Comedy Seen)</Text>
                                            <Text className={classes.resultValue}>${animCostTotal.toFixed(2)}</Text>
                                        </div>
                                    )}

                                    <div className={classes.resultRow} style={{ borderBottom: 'none' }}>
                                        <Text className={classes.resultLabel}>% of revenue paid in fees</Text>
                                        <Text className={classes.resultValue}>{animCostPct.toFixed(1)}%</Text>
                                    </div>
                                </Stack>
                            </Box>
                        </SimpleGrid>
                    </Stack>
                </Card>
            </Center>

            <SectionDivider label="Compare with Eventbrite" />

            {/* ── Comparison table card ── */}
            <Center>
                <Card
                    padding='xl'
                    radius='xl'
                    withBorder
                    shadow='sm'
                    className={classes.card}
                    sx={{ borderLeft: '4px solid #532d86' }}
                >
                    <Group spacing="xs" position="center" mb="xs">
                        <IconArrowsLeftRight size={22} color="#532d86" />
                        <Text className={classes.cardTitle}>
                            Compare with{' '}
                            <Anchor component={Link} to="/pricingproof" sx={{ color: '#532d86' }}>Eventbrite</Anchor>
                        </Text>
                    </Group>

                    <Box mt="lg" sx={{ overflowX: 'auto' }}>
                        <Table className={classes.compTable} striped highlightOnHover>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Metric</th>
                                    <th>
                                        <Badge color="green" variant="filled">Comedy Seen</Badge>
                                    </th>
                                    <th>
                                        <Badge color="gray" variant="filled">Eventbrite</Badge>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray' }}>Total revenue generated</td>
                                    <td className={classes.csCell} style={{ color: '#28a745' }}>${animTotalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className={classes.ebCell} style={{ color: '#28a745' }}>${animTotalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray' }}>Total fees</td>
                                    <td className={classes.csCell}>${animCostTotal.toFixed(2)}</td>
                                    <td className={classes.ebCell}>${animEventbriteCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray', paddingLeft: rem(16) }}>Per-ticket fee</td>
                                    <td className={classes.csCell}>${animCostPerTicket.toFixed(2)}</td>
                                    <td className={classes.ebCell}>${animEventbriteCostPerTicket.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray' }}>% of revenue</td>
                                    <td className={classes.csCell}>{animCostPct.toFixed(1)}%</td>
                                    <td className={classes.ebCell}>{animEbPct.toFixed(1)}%</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Box>

                    {/* Savings callout */}
                    <div className={classes.savingsCallout}>
                        <Text className={classes.savingsLabel}>You save with Comedy Seen</Text>
                        <Text className={classes.savingsAmount}>${savings.toFixed(2)}</Text>
                        <Text className={classes.savingsSubtext}>
                            vs. Eventbrite alone on {inputSold} tickets at ${inputPrice} each
                        </Text>
                    </div>
                </Card>
            </Center>

            <SectionDivider label="Hybrid approach" />

            {/* ── Hybrid card ── */}
            <Center>
                <Card
                    padding='xl'
                    radius='xl'
                    withBorder
                    shadow='sm'
                    className={classes.card}
                    sx={{ borderLeft: '4px solid #f0a500' }}
                >
                    <Text className={classes.cardTitle}>Use Both!</Text>
                    <Text size="sm" color="dimmed" ta="center" mt={4}>
                        Still want Eventbrite's discoverability? Use Comedy Seen for direct sales and Eventbrite for search traffic.
                    </Text>

                    <Group position='center' align='center' mt="lg" sx={{ flexWrap: 'wrap' }}>
                        <Text size="sm" ta="center" sx={{ maxWidth: rem(340) }}>
                            Estimated % of audience from Eventbrite search <em>(default 30%)</em>
                        </Text>
                        <Group spacing='xs'>
                            <NumberInput
                                value={evPercent}
                                onChange={setEvPercent}
                                sx={{ width: rem(80) }}
                                min={0}
                                max={100}
                            />
                            <Text>%</Text>
                        </Group>
                    </Group>

                    <Space h="md" />

                    <Box sx={{ overflowX: 'auto' }}>
                        <Table className={classes.compTable} striped highlightOnHover>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Metric</th>
                                    <th><Badge color="yellow" variant="filled">Hybrid</Badge></th>
                                    <th><Badge color="gray" variant="filled">Eventbrite only</Badge></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray' }}>Total revenue generated</td>
                                    <td className={classes.csCell} style={{ color: '#28a745' }}>${animTotalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className={classes.ebCell} style={{ color: '#28a745' }}>${animTotalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray' }}>{stripe ? 'Total fees (Comedy Seen + Stripe)' : 'Total fees'}</td>
                                    <td className={classes.csCell}>${animHybridCost.toFixed(2)}</td>
                                    <td className={classes.ebCell}>${animEventbriteCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray', paddingLeft: rem(16) }}>Per-ticket fee</td>
                                    <td className={classes.csCell}>${animHybridCostPerTicket.toFixed(2)}</td>
                                    <td className={classes.ebCell}>${animEventbriteCostPerTicket.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: 'left', fontSize: rem(13), color: 'gray' }}>% of revenue</td>
                                    <td className={classes.csCell}>{animHyPct.toFixed(1)}%</td>
                                    <td className={classes.ebCell}>{animEbPct.toFixed(1)}%</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Box>

                    <div className={classes.savingsCallout} style={{ background: 'linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%)', borderColor: '#f0a500' }}>
                        <Text className={classes.savingsLabel} sx={{ color: '#7a4f00' }}>Hybrid saves you vs. Eventbrite only</Text>
                        <Text className={classes.savingsAmount} sx={{ color: '#7a4f00' }}>${hybridSavings.toFixed(2)}</Text>
                        <Text className={classes.savingsSubtext} sx={{ color: '#a06a00' }}>
                            on {inputSold} tickets at ${inputPrice} each ({evPercent}% via Eventbrite)
                        </Text>
                    </div>
                </Card>
            </Center>

            <Space h="xl" />
            <Space h="md" />

            <Center>
                <Button
                    color='green'
                    size='lg'
                    component='a'
                    href='https://cal.com/pete-pobblebonk-yef5rr/30min'
                    type='button'
                    className={classes.ctaButton}
                >
                    Book a demo!
                </Button>
            </Center>

            <Space h="xl" />

            <Footer links={links} />
        </body>
    )
}

export default Pricing